import React from 'react';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { START_NODE_RADIUS, STATE_COLOURS, TRANSITION_STROKE_WIDTH } from './ellipsis.tsx';
import { NODE_TYPE } from './directionKeys.tsx';
import type { Point } from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import MoveArrow, { ARROW_RADIUS } from './MoveArrow.tsx';
import { useDraggableDataAttributes } from './useCanvasDragDropEvents.tsx';

const startNodeLabel = defineMessage({
	id: 'accessible-workflow-diagram.diagram.start-node.start-node-label',
	defaultMessage: 'START',
	description: 'Capitalised label for the start node in the workflow.',
});

const TEXT_LINE_HEIGHT_OFFSET = 2.75;
const FOCUS_RING_WIDTH = 4;

const ARROW_PADDING = FOCUS_RING_WIDTH + ARROW_RADIUS;

interface Props {
	id: string;
	position?: Point;
	isImmutable: boolean;
	isTargeted?: boolean;
	isMoving?: boolean;
	onHover: (item: ItemReference | undefined) => void;
}

const StartNode = ({
	position = { x: 0, y: 0 },
	id,
	isImmutable,
	isTargeted,
	isMoving,
	onHover,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const dataAttributes = useDraggableDataAttributes('start-node', id);

	const onMouseEnter = () => {
		onHover({ id, type: NODE_TYPE });
	};

	const onMouseLeave = () => {
		onHover(undefined);
	};

	return (
		<>
			{isTargeted && (
				<ellipse
					cx={position.x + START_NODE_RADIUS}
					cy={position.y + START_NODE_RADIUS}
					rx={START_NODE_RADIUS + FOCUS_RING_WIDTH / 2}
					ry={START_NODE_RADIUS + FOCUS_RING_WIDTH / 2}
					stroke={STATE_COLOURS.focused}
					fillOpacity="0"
					strokeWidth={FOCUS_RING_WIDTH}
				/>
			)}
			{isMoving && (
				<>
					<MoveArrow
						x={position.x + START_NODE_RADIUS}
						y={position.y - ARROW_PADDING}
						direction="up"
					/>
					<MoveArrow
						x={position.x + START_NODE_RADIUS}
						y={position.y + START_NODE_RADIUS * 2 + ARROW_PADDING}
						direction="down"
					/>
					<MoveArrow
						x={position.x - ARROW_PADDING}
						y={position.y + START_NODE_RADIUS}
						direction="left"
					/>
					<MoveArrow
						x={position.x + START_NODE_RADIUS * 2 + ARROW_PADDING}
						y={position.y + START_NODE_RADIUS}
						direction="right"
					/>
				</>
			)}
			<g
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				cursor={isImmutable ? undefined : 'move'}
				{...dataAttributes}
			>
				<ellipse
					cx={position.x + START_NODE_RADIUS}
					cy={position.y + START_NODE_RADIUS - TRANSITION_STROKE_WIDTH}
					rx={START_NODE_RADIUS}
					ry={START_NODE_RADIUS}
					fill={token('color.background.neutral.bold')}
				/>
				<text
					x={position.x + START_NODE_RADIUS}
					y={position.y + START_NODE_RADIUS - TRANSITION_STROKE_WIDTH}
					textAnchor="middle"
					fontSize="8px"
					fill={token('color.text.inverse')}
					fontWeight="bold"
				>
					<tspan dy={TEXT_LINE_HEIGHT_OFFSET}>{formatMessage(startNodeLabel)}</tspan>
				</text>
			</g>
		</>
	);
};

export default StartNode;
